import React, { useState, useEffect } from 'react';
import { ChevronDown, Save, Building2, FileText, CheckCircle, MapPin, User, Users, ChevronRight, AlertCircle, Briefcase, Plus, X } from 'lucide-react';
import { useDropdownData } from '../utils/useDropdownData';
import { establishmentAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import DateInput from './DateInput';
import './UpdateEmployee.css';
import './AttendanceRegistry.css';

// Fields that require uniqueness validation
const UNIQUE_FIELDS = {
  code: 'Organization Code',
  gstin: 'GSTIN',
  pan: 'PAN',
  esicCode: 'ESIC Code',
  epfCode: 'EPF Code',
  bankAccountNumber: 'Bank Account Number',
  mainEmail: 'Main Office Email',
  registeredEmail: 'Registered Office Email'
};

const UpdateEstablishment = () => {
  const [selectedEstablishment, setSelectedEstablishment] = useState('');
  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [uniqueErrors, setUniqueErrors] = useState({});
  const [departments, setDepartments] = useState([]);
  const [newDepartment, setNewDepartment] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadMessage, setUploadMessage] = useState({ type: '', text: '' });
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Use the custom hook to fetch establishments
  const {
    establishments,
    loading,
    errors,
    fetchEstablishments
  } = useDropdownData();

  // Fetch establishments on component mount
  useEffect(() => {
    fetchEstablishments();
  }, [fetchEstablishments]);

  // Fetch attachments when establishment is selected
  useEffect(() => {
    if (selectedEstablishmentId) {
      fetchAttachments(selectedEstablishmentId);
    } else {
      setAttachments([]);
    }
  }, [selectedEstablishmentId]);

  // Auto-select first establishment when establishments are loaded
  useEffect(() => {
    if (establishments.length > 0 && !selectedEstablishmentId) {
      const firstEstablishmentId = establishments[0].id;
      // Simulate the selection change event
      handleEstablishmentChange({ target: { value: firstEstablishmentId } });
    }
  }, [establishments]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    { id: 'basic', name: 'Basic Info', icon: Building2 },
    { id: 'departments', name: 'Departments', icon: Briefcase },
    { id: 'addresses', name: 'Addresses', icon: MapPin },
    { id: 'employer', name: 'Employer', icon: User },
    { id: 'management', name: 'Management', icon: Users }
  ];

  const sectionFields = {
    'basic': [
      { name: 'establishmentName', label: 'Name of the Organization', type: 'text', defaultValue: 'Nestien Shipment Management' }
    ],
    'addresses': [
      // Main Establishment
      { name: 'mainLocation', label: 'Main Organization - Location/City', type: 'text', defaultValue: 'Bangalore' },
      { name: 'mainAddress', label: 'Main Organization - Full Address', type: 'textarea', defaultValue: 'Site: DBOI Global Services Pvt. Ltd\nBlock V, VI, VII, VIII Velankani Tech Park\nNo.43, Electronic City, Phase I,\nHosur Road, Bangalore - 560 100' },
      { name: 'mainTelephone', label: 'Main Organization - Telephone', type: 'tel', defaultValue: '22 24148999' },
      { name: 'mainFax', label: 'Main Organization - Fax', type: 'tel', defaultValue: '22 65118999' },
      { name: 'mainEmail', label: 'Main Organization - E-mail', type: 'email', defaultValue: 'info@nestien.com' },

      // Registered Office
      { name: 'registeredLocation', label: 'Registered Office - Location/City', type: 'text', defaultValue: 'Mumbai' },
      { name: 'registeredAddress', label: 'Registered Office - Full Address', type: 'textarea', defaultValue: 'A-237, Antop Hills Warehouse Complex,\nBarkatali Nak, Near Dosti Acres,\nOff: Imax Road, Antop Hills\nWadala (E),' },
      { name: 'registeredTelephone', label: 'Registered Office - Telephone', type: 'tel', defaultValue: '22 24148999' },
      { name: 'registeredFax', label: 'Registered Office - Fax', type: 'tel', defaultValue: '22 65118999' },
      { name: 'registeredEmail', label: 'Registered Office - E-mail', type: 'email', defaultValue: 'info@nestien.com' }
    ],
    'employer': [
      { name: 'employerName', label: 'Employer Name', type: 'text', defaultValue: 'Suresh Kumar Raju' },
      { name: 'employerDesignation', label: 'Designation', type: 'text', defaultValue: 'Proprietor' },
      { name: 'employerResidentialAddress', label: 'Residential Address', type: 'textarea', defaultValue: 'Flat No.202, B Wing, Comet Apartment,\nBhakti Park, Near I Max Cinema,\nWadala East, Mumbai, Antop Hill,\nMumbai, Maharashtra - 400037.' },
      { name: 'employerTelephone', label: 'Telephone', type: 'tel', defaultValue: '(O) 22 24148999' },
      { name: 'employerMobile', label: 'Mobile', type: 'tel', defaultValue: '9819731611' },
      { name: 'employerEmail', label: 'E-mail', type: 'email', defaultValue: 'info@nestien.com' }
    ],
    'management': [
      // Manager
      { name: 'managerName', label: 'Manager - Name', type: 'text', defaultValue: 'DASS DURAIRAJ' },
      { name: 'managerDesignation', label: 'Manager - Designation', type: 'text', defaultValue: 'Manager' },
      { name: 'managerResidentialAddress', label: 'Manager - Residential Address', type: 'textarea', defaultValue: 'No.31, Bazaar Street, Vannerpet,\nVivek Nagar Post,\nBangalore - 560047.' },
      { name: 'managerTelephone', label: 'Manager - Telephone', type: 'tel', defaultValue: '(O)' },
      { name: 'managerMobile', label: 'Manager - Mobile', type: 'tel', defaultValue: '9886542008' },
      { name: 'managerEmail', label: 'Manager - E-mail', type: 'email', defaultValue: 'dass.d@nestien.com' },

      // Authorised Signatory
      { name: 'authSignatoryName', label: 'Authorised Signatory - Name', type: 'text', defaultValue: 'DASS DURIRAJ' },
      { name: 'authSignatoryDesignation', label: 'Authorised Signatory - Designation', type: 'text', defaultValue: 'Authorised Signatory' },
      { name: 'authSignatoryResidentialAddress', label: 'Authorised Signatory - Residential Address', type: 'textarea', defaultValue: 'No.31, Bazaar Street, Vannerpet,\nVivek Nagar Post,\nBangalore - 560047.' },
      { name: 'authSignatoryTelephone', label: 'Authorised Signatory - Telephone', type: 'tel', defaultValue: '(O)' },
      { name: 'authSignatoryMobile', label: 'Authorised Signatory - Mobile', type: 'tel', defaultValue: '' },
      { name: 'authSignatoryEmail', label: 'Authorised Signatory - E-mail', type: 'email', defaultValue: '' }
    ]
  };

  const handleEstablishmentChange = async (e) => {
    const selectedId = e.target.value;
    setSelectedEstablishmentId(selectedId);
    setActiveTab('basic');

    if (!selectedId) {
      setSelectedEstablishment('');
      setFormData({});
      return;
    }

    setIsLoadingDetails(true);
    try {
      console.log('Fetching all organizations for details...');
      const response = await establishmentAPI.getAll();
      const allEstablishments = response.establishments || response || [];
      console.log('Fetched organizations:', allEstablishments);

      // Find the selected establishment from the complete list
      // Use string comparison to avoid type mismatch issues
      const fullDetails = allEstablishments.find(est => String(est.id) === String(selectedId));

      if (!fullDetails) {
        throw new Error(`Organization with ID ${selectedId} not found in the list`);
      }

      // Map the establishment data to form fields (using snake_case from API)
      console.log('Full organization details found:', fullDetails);
      console.log('Contract dates from API:', {
        start: fullDetails.contract_start_date,
        end: fullDetails.contract_valid_till,
        tenure: fullDetails.tenure
      });

      const establishmentData = {
        // Basic
        establishmentName: fullDetails.name || '',
        contractStartDate: fullDetails.contract_start_date || '',
        contractValidTill: fullDetails.contract_valid_till || '',
        tenure: fullDetails.tenure || calculateTenure(fullDetails.contract_start_date, fullDetails.contract_valid_till),

        // Tax/Compliance
        gstin: fullDetails.gstin || '',
        pan: fullDetails.pan || '',
        esicCode: fullDetails.esic_code || '',
        epfCode: fullDetails.epf_code || '',

        // Bank Account Details
        bankName: fullDetails.bank_name || '',
        bankBranch: fullDetails.bank_branch || '',
        bankAccountNumber: fullDetails.bank_account_number || '',
        bankIfsc: fullDetails.bank_ifsc || '',

        // Addresses
        mainLocation: fullDetails.main_location || '',
        mainAddress: fullDetails.main_address || '',
        mainTelephone: fullDetails.main_telephone || '',
        mainFax: fullDetails.main_fax || '',
        mainEmail: fullDetails.main_email || '',
        registeredLocation: fullDetails.registered_location || '',
        registeredAddress: fullDetails.registered_address || '',
        registeredTelephone: fullDetails.registered_telephone || '',
        registeredFax: fullDetails.registered_fax || '',
        registeredEmail: fullDetails.registered_email || '',

        // Employer
        employerName: fullDetails.employer_name || '',
        employerDesignation: fullDetails.employer_designation || '',
        employerResidentialAddress: fullDetails.employer_residential_address || '',
        employerTelephone: fullDetails.employer_telephone || '',
        employerMobile: fullDetails.employer_mobile || '',
        employerEmail: fullDetails.employer_email || '',

        // Management
        managerName: fullDetails.manager_name || '',
        managerDesignation: fullDetails.manager_designation || '',
        managerResidentialAddress: fullDetails.manager_residential_address || '',
        managerTelephone: fullDetails.manager_telephone || '',
        managerMobile: fullDetails.manager_mobile || '',
        managerEmail: fullDetails.manager_email || '',
        authSignatoryName: fullDetails.auth_signatory_name || '',
        authSignatoryDesignation: fullDetails.auth_signatory_designation || '',
        authSignatoryResidentialAddress: fullDetails.auth_signatory_residential_address || '',
        authSignatoryTelephone: fullDetails.auth_signatory_telephone || '',
        authSignatoryMobile: fullDetails.auth_signatory_mobile || '',
        authSignatoryEmail: fullDetails.auth_signatory_email || ''
      };

      // Parse departments from JSON string
      let departmentsList = [];
      if (fullDetails.departments) {
        try {
          departmentsList = JSON.parse(fullDetails.departments);
        } catch (e) {
          console.error('Error parsing departments:', e);
          departmentsList = [];
        }
      }
      setDepartments(departmentsList);

      // Store the full details for later use (like preserving code, state, etc.)
      setSelectedEstablishment(fullDetails);
      setFormData(establishmentData);
    } catch (error) {
      console.error('Error loading organization details:', error);
      alert('Error loading organization details: ' + error.message);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const calculateTenure = (startDate, endDate) => {
    if (!startDate || !endDate) return '';

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end < start) return 'Invalid dates';

    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    const parts = [];
    if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
    if (days > 0 && years === 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);

    return parts.length > 0 ? parts.join(', ') : '0 days';
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [fieldName]: value
      };

      // Auto-calculate tenure when dates change
      if (fieldName === 'contractStartDate' || fieldName === 'contractValidTill') {
        updated.tenure = calculateTenure(
          fieldName === 'contractStartDate' ? value : prev.contractStartDate,
          fieldName === 'contractValidTill' ? value : prev.contractValidTill
        );
      }

      return updated;
    });

    // Clear uniqueness error when value changes
    if (UNIQUE_FIELDS[fieldName] && uniqueErrors[fieldName]) {
      setUniqueErrors(prev => ({ ...prev, [fieldName]: null }));
    }
  };

  // Validate field uniqueness on blur
  const validateUniqueness = async (field, value) => {
    if (!UNIQUE_FIELDS[field] || !value || !value.trim()) {
      setUniqueErrors(prev => ({ ...prev, [field]: null }));
      return true;
    }

    // Don't validate if there's already an error for this field to prevent repeated alerts
    if (uniqueErrors[field]) {
      return false;
    }

    try {
      const result = await establishmentAPI.checkUnique(field, value, selectedEstablishmentId);
      if (!result.is_unique) {
        const errorMsg = `${UNIQUE_FIELDS[field]} "${value}" is already registered to ${result.existing_establishment.name}`;
        setUniqueErrors(prev => ({ ...prev, [field]: errorMsg }));

        // Alert once and focus back on the field
        alert(errorMsg);

        // Focus back on the field
        setTimeout(() => {
          const inputElement = document.querySelector(`input[value="${value}"]`) ||
            document.querySelector(`[name="${field}"]`);
          if (inputElement) {
            inputElement.focus();
            inputElement.select();
          }
        }, 100);

        return false;
      }
      setUniqueErrors(prev => ({ ...prev, [field]: null }));
      return true;
    } catch (error) {
      console.error('Error checking uniqueness:', error);
      return true; // Allow submission if check fails
    }
  };

  const handleAddDepartment = () => {
    if (newDepartment.trim() && !departments.includes(newDepartment.trim())) {
      setDepartments([...departments, newDepartment.trim()]);
      setNewDepartment('');
    } else if (departments.includes(newDepartment.trim())) {
      alert('Department already exists');
    }
  };

  const handleRemoveDepartment = (dept) => {
    setDepartments(departments.filter(d => d !== dept));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDepartment();
    }
  };

  // Attachment handling functions
  const fetchAttachments = async (establishmentId) => {
    if (!establishmentId) return;

    try {
      const response = await establishmentAPI.getAttachments(establishmentId);
      setAttachments(response.attachments || []);
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);

    if (!selectedEstablishmentId) {
      setUploadMessage({ type: 'error', text: 'Please select an organization first' });
      return;
    }

    for (const file of files) {
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setUploadMessage({ type: 'error', text: `${file.name} is too large. Maximum size is 10MB` });
        continue;
      }

      setUploadingFile(true);
      setUploadMessage({ type: '', text: '' });

      try {
        const response = await establishmentAPI.uploadAttachment(file, selectedEstablishmentId, selectedCategory);
        const categoryText = selectedCategory ? ` as ${selectedCategory}` : ' as general';
        setUploadMessage({ type: 'success', text: `${file.name} uploaded successfully${categoryText}` });

        // Refresh attachments list
        await fetchAttachments(selectedEstablishmentId);

        // Clear message after 3 seconds
        setTimeout(() => setUploadMessage({ type: '', text: '' }), 3000);
      } catch (error) {
        console.error('Upload error:', error);
        setUploadMessage({ type: 'error', text: error.message || `Failed to upload ${file.name}` });
      } finally {
        setUploadingFile(false);
      }
    }

    // Reset file input
    e.target.value = '';
  };

  const handleDeleteAttachment = async (attachmentId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete ${fileName}?`)) {
      return;
    }

    try {
      await establishmentAPI.deleteAttachment(selectedEstablishmentId, attachmentId);
      setUploadMessage({ type: 'success', text: `${fileName} deleted successfully` });

      // Refresh attachments list
      await fetchAttachments(selectedEstablishmentId);

      // Clear message after 3 seconds
      setTimeout(() => setUploadMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Delete error:', error);
      setUploadMessage({ type: 'error', text: error.message || `Failed to delete ${fileName}` });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
      return '🖼️';
    } else if (ext === 'pdf') {
      return '📄';
    } else if (['doc', 'docx'].includes(ext)) {
      return '📝';
    } else if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return '📊';
    } else if (ext === 'txt') {
      return '📋';
    }
    return '📎';
  };

  const handleNext = () => {
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    if (currentIndex < tabs.length - 1) {
      const nextTabId = tabs[currentIndex + 1].id;
      setActiveTab(nextTabId);

      // Scroll the active tab into view on mobile
      setTimeout(() => {
        const tabElement = document.querySelector(`.new-employee-tab.active`);
        if (tabElement) {
          tabElement.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }, 100);
    }
  };

  const handlePrevious = () => {
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    if (currentIndex > 0) {
      const prevTabId = tabs[currentIndex - 1].id;
      setActiveTab(prevTabId);

      // Scroll the active tab into view on mobile
      setTimeout(() => {
        const tabElement = document.querySelector(`.new-employee-tab.active`);
        if (tabElement) {
          tabElement.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }, 100);
    }
  };

  // Validate current tab fields
  const validateCurrentTab = () => {
    const errors = [];

    // Check for unique field errors in current tab only
    const tabUniqueFields = {
      'basic': ['code', 'gstin', 'pan', 'esicCode', 'epfCode', 'bankAccountNumber'],
      'departments': [],
      'addresses': ['mainEmail', 'registeredEmail'],
      'employer': [],
      'management': []
    };

    const currentTabUniqueFields = tabUniqueFields[activeTab] || [];
    const tabUniqueErrors = currentTabUniqueFields.filter(field => uniqueErrors[field]);

    if (tabUniqueErrors.length > 0) {
      const errorFieldNames = tabUniqueErrors.map(field => UNIQUE_FIELDS[field]).join(', ');
      errors.push(`Duplicate values found in ${errorFieldNames}`);
    }

    // Add any tab-specific required field validation here if needed
    // For UpdateEstablishment, most fields are optional

    return errors;
  };

  const handleSubmit = async () => {
    // Validate only current tab
    const validationErrors = validateCurrentTab();
    if (validationErrors.length > 0) {
      alert(`Please fix the following errors on this tab:\n\n${validationErrors.join('\n')}`);
      return;
    }

    setIsSubmitting(true);

    // Convert form data to API format - match what API expects (same as AddEstablishment)
    const payload = {
      name: formData.establishmentName,
      code: selectedEstablishment?.code || '', // Preserve existing code, don't allow editing
      branch: formData.mainLocation || selectedEstablishment?.branch || 'Main',
      address: formData.mainAddress || '',
      city: formData.mainLocation || selectedEstablishment?.city || '',
      state: selectedEstablishment?.state || '',
      zipCode: selectedEstablishment?.zip_code || selectedEstablishment?.zipCode || '',
      contractStartDate: formData.contractStartDate,
      contractValidTill: formData.contractValidTill,
      tenure: formData.tenure,
      departments: JSON.stringify(departments),

      // Tax/Compliance fields
      gstin: formData.gstin || null,
      pan: formData.pan || null,
      esicCode: formData.esicCode || null,
      epfCode: formData.epfCode || null,

      // Bank account fields
      bankName: formData.bankName || null,
      bankBranch: formData.bankBranch || null,
      bankAccountNumber: formData.bankAccountNumber || null,
      bankIfsc: formData.bankIfsc || null,

      // Extended fields
      mainLocation: formData.mainLocation,
      mainAddress: formData.mainAddress,
      mainTelephone: formData.mainTelephone,
      mainFax: formData.mainFax,
      mainEmail: formData.mainEmail,
      registeredLocation: formData.registeredLocation,
      registeredAddress: formData.registeredAddress,
      registeredTelephone: formData.registeredTelephone,
      registeredFax: formData.registeredFax,
      registeredEmail: formData.registeredEmail,
      employerName: formData.employerName,
      employerDesignation: formData.employerDesignation,
      employerResidentialAddress: formData.employerResidentialAddress,
      employerTelephone: formData.employerTelephone,
      employerMobile: formData.employerMobile,
      employerEmail: formData.employerEmail,
      managerName: formData.managerName,
      managerDesignation: formData.managerDesignation,
      managerResidentialAddress: formData.managerResidentialAddress,
      managerTelephone: formData.managerTelephone,
      managerMobile: formData.managerMobile,
      managerEmail: formData.managerEmail,
      authSignatoryName: formData.authSignatoryName,
      authSignatoryDesignation: formData.authSignatoryDesignation,
      authSignatoryResidentialAddress: formData.authSignatoryResidentialAddress,
      authSignatoryTelephone: formData.authSignatoryTelephone,
      authSignatoryMobile: formData.authSignatoryMobile,
      authSignatoryEmail: formData.authSignatoryEmail
    };

    try {
      console.log('Sending payload to API:', JSON.stringify(payload, null, 2));

      // Call the API to update establishment
      const response = await establishmentAPI.update(selectedEstablishmentId, payload);

      notifySavedAndReload('✓ Organization details updated successfully!');
      console.log('API Response:', response);

      // Refresh the organizations list
      await fetchEstablishments();
    } catch (error) {
      console.error('Error updating organization:', error);
      alert('Error updating organization details: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLastTab = activeTab === 'management';

  return (
    <div className="update-employee-container">
      <div className="update-employee-wrapper">
        <div className="update-employee-header">
          <h2 className="update-employee-title">Update Organization</h2>
          <p className="update-employee-subtitle">Select organization to update details</p>
        </div>

        {/* Selection Filters */}
        <div className="attendance-grid-filters">
          {/* Establishment Dropdown */}
          <div className="attendance-filter-group">
            <label className="attendance-filter-label">
              <span className="filter-icon-wrapper establishment">
                <Building2 size={15} />
              </span>
              Organization
            </label>
            {/* Loading State */}
            {loading.establishments && (
              <div style={{ padding: '0.5rem', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
                Loading organizations...
              </div>
            )}

            {/* Error State */}
            {errors.establishments && !loading.establishments && (
              <div style={{
                padding: '0.5rem',
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#991b1b',
                fontSize: '0.75rem'
              }}>
                <AlertCircle size={14} />
                <span>Error: {errors.establishments}</span>
              </div>
            )}

            {/* Dropdown */}
            {!loading.establishments && !errors.establishments && (
              <div style={{ position: 'relative', width: '100%', minWidth: '300px' }}>
                <select
                  value={selectedEstablishmentId}
                  onChange={handleEstablishmentChange}
                  className="attendance-filter-select"
                  style={{ appearance: 'none', paddingRight: '2rem', width: '100%' }}
                  disabled={loading.establishments}
                >
                  <option value="">Select Organization</option>
                  {establishments.map((est) => (
                    <option key={est.id} value={est.id}>
                      {est.name}
                    </option>
                  ))}
                </select>
                <ChevronDown size={16} style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#6b7280'
                }} />
              </div>
            )}
          </div>
        </div>

        {/* Multi-step Form with Tabs */}
        {selectedEstablishment && (
          <div className="update-employee-form-card animate-fadeIn">
            {/* Loading Details State */}
            {isLoadingDetails && (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                Loading organization details...
              </div>
            )}

            {!isLoadingDetails && (
              <>
                {/* Establishment Badge */}
                <div className="update-employee-section-header" style={{ marginBottom: '1.5rem' }}>
                  <div className="update-employee-employee-badge">
                    Updating: {selectedEstablishment?.name || 'Unknown Organization'}
                  </div>
                </div>

                {/* Tabs */}
                <div className="new-employee-tabs" style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', borderBottom: '2px solid #e5e7eb' }}>
                  {tabs.map(tab => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={(e) => {
                          setActiveTab(tab.id);
                          // Scroll the clicked tab into view on mobile
                          setTimeout(() => {
                            e.target.closest('button').scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                          }, 100);
                        }}
                        className={`new-employee-tab ${activeTab === tab.id ? 'active' : ''}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.75rem 1.5rem',
                          background: activeTab === tab.id ? '#10b981' : 'transparent',
                          color: activeTab === tab.id ? 'white' : '#6b7280',
                          border: 'none',
                          borderRadius: '8px 8px 0 0',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: activeTab === tab.id ? '600' : '500',
                          transition: 'all 0.2s',
                          position: 'relative'
                        }}
                      >
                        <Icon size={16} />
                        {tab.name}
                        {activeTab === tab.id && (
                          <div style={{
                            position: 'absolute',
                            bottom: '-2px',
                            left: 0,
                            right: 0,
                            height: '2px',
                            background: '#10b981'
                          }}></div>
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="new-employee-content">
                  {/* Basic Information Tab */}
                  {activeTab === 'basic' && (
                    <div className="animate-fadeIn">
                      <h3 className="new-employee-section-header" style={{ marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: '600' }}>Basic Information</h3>
                      <p className="new-employee-section-description" style={{ marginBottom: '1.5rem', color: '#6b7280', fontSize: '0.875rem' }}>Update the organization's basic details.</p>

                      <div className="update-employee-form-grid">
                        <div className="full-width">
                          <label className="update-employee-field-label">Name of the Organization</label>
                          <input
                            type="text"
                            value={formData.establishmentName || ''}
                            onChange={(e) => handleInputChange('establishmentName', e.target.value)}
                            className="update-employee-input"
                          />
                        </div>

                        <div>
                          <label className="update-employee-field-label">Contract Start Date</label>
                          <DateInput
                            value={formData.contractStartDate || ''}
                            onChange={(e) => handleInputChange('contractStartDate', e.target.value)}
                            className="update-employee-input"
                          />
                        </div>

                        <div>
                          <label className="update-employee-field-label">Contract Valid Till</label>
                          <DateInput
                            value={formData.contractValidTill || ''}
                            onChange={(e) => handleInputChange('contractValidTill', e.target.value)}
                            className="update-employee-input"
                          />
                        </div>

                        <div className="full-width">
                          <label className="update-employee-field-label">Contract Period / Tenure (Auto-calculated)</label>
                          <input
                            type="text"
                            value={formData.tenure || ''}
                            className="update-employee-input"
                            placeholder="Select start and end dates to calculate"
                            readOnly
                            style={{ backgroundColor: 'var(--theme-surface)', cursor: 'not-allowed' }}
                          />
                          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            Automatically calculated from contract dates
                          </p>
                        </div>
                      </div>

                      {/* Tax/Compliance Information */}
                      <h3 className="new-employee-section-header" style={{ marginTop: '2rem', marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: '600' }}>Tax & Compliance Information</h3>
                      <p className="new-employee-section-description" style={{ marginBottom: '1.5rem', color: '#6b7280', fontSize: '0.875rem' }}>GSTIN, PAN, ESIC and EPF codes for invoicing and compliance.</p>

                      <div className="update-employee-form-grid">
                        <div>
                          <label className="update-employee-field-label">GSTIN (GST Identification Number)</label>
                          <input
                            type="text"
                            value={formData.gstin || ''}
                            onChange={(e) => handleInputChange('gstin', e.target.value.toUpperCase())}
                            onBlur={(e) => validateUniqueness('gstin', e.target.value)}
                            className={`update-employee-input ${uniqueErrors.gstin ? 'input-error' : ''}`}
                            placeholder="e.g., 29AACFE8938R1ZG"
                            maxLength={15}
                          />
                          {uniqueErrors.gstin && <span className="error-text">{uniqueErrors.gstin}</span>}
                          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            15-character alphanumeric code
                          </p>
                        </div>
                        <div>
                          <label className="update-employee-field-label">PAN (Permanent Account Number)</label>
                          <input
                            type="text"
                            value={formData.pan || ''}
                            onChange={(e) => handleInputChange('pan', e.target.value.toUpperCase())}
                            onBlur={(e) => validateUniqueness('pan', e.target.value)}
                            className={`update-employee-input ${uniqueErrors.pan ? 'input-error' : ''}`}
                            placeholder="e.g., AACFE8938R"
                            maxLength={10}
                          />
                          {uniqueErrors.pan && <span className="error-text">{uniqueErrors.pan}</span>}
                          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            10-character alphanumeric code
                          </p>
                        </div>
                        <div>
                          <label className="update-employee-field-label">ESIC Code</label>
                          <input
                            type="text"
                            value={formData.esicCode || ''}
                            onChange={(e) => handleInputChange('esicCode', e.target.value)}
                            onBlur={(e) => validateUniqueness('esicCode', e.target.value)}
                            className={`update-employee-input ${uniqueErrors.esicCode ? 'input-error' : ''}`}
                            placeholder="e.g., 41000263690000999"
                          />
                          {uniqueErrors.esicCode && <span className="error-text">{uniqueErrors.esicCode}</span>}
                          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            ESIC organization code
                          </p>
                        </div>
                        <div>
                          <label className="update-employee-field-label">EPF Code</label>
                          <input
                            type="text"
                            value={formData.epfCode || ''}
                            onChange={(e) => handleInputChange('epfCode', e.target.value)}
                            onBlur={(e) => validateUniqueness('epfCode', e.target.value)}
                            className={`update-employee-input ${uniqueErrors.epfCode ? 'input-error' : ''}`}
                            placeholder="e.g., VDBRD0065200000"
                          />
                          {uniqueErrors.epfCode && <span className="error-text">{uniqueErrors.epfCode}</span>}
                          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            EPF organization code
                          </p>
                        </div>
                      </div>

                      {/* Bank Account Details Section */}
                      <h3 className="new-employee-section-header" style={{ marginTop: '2rem' }}>Bank Account Details</h3>
                      <p className="new-employee-section-description">Bank details for invoice payments.</p>

                      <div className="new-employee-field-group">
                        <div>
                          <label className="update-employee-field-label">Bank Name</label>
                          <input
                            type="text"
                            value={formData.bankName || ''}
                            onChange={(e) => handleInputChange('bankName', e.target.value)}
                            className="update-employee-input"
                            placeholder="e.g., State Bank of India"
                          />
                        </div>
                        <div>
                          <label className="update-employee-field-label">Branch Name</label>
                          <input
                            type="text"
                            value={formData.bankBranch || ''}
                            onChange={(e) => handleInputChange('bankBranch', e.target.value)}
                            className="update-employee-input"
                            placeholder="e.g., Main Branch, Mumbai"
                          />
                        </div>
                      </div>

                      <div className="new-employee-field-group">
                        <div>
                          <label className="update-employee-field-label">Account Number</label>
                          <input
                            type="text"
                            value={formData.bankAccountNumber || ''}
                            onChange={(e) => handleInputChange('bankAccountNumber', e.target.value)}
                            onBlur={(e) => validateUniqueness('bankAccountNumber', e.target.value)}
                            className={`update-employee-input ${uniqueErrors.bankAccountNumber ? 'input-error' : ''}`}
                            placeholder="e.g., 1234567890123456"
                          />
                          {uniqueErrors.bankAccountNumber && <span className="error-text">{uniqueErrors.bankAccountNumber}</span>}
                        </div>
                        <div>
                          <label className="update-employee-field-label">IFSC Code</label>
                          <input
                            type="text"
                            value={formData.bankIfsc || ''}
                            onChange={(e) => handleInputChange('bankIfsc', e.target.value.toUpperCase())}
                            className="update-employee-input"
                            placeholder="e.g., SBIN0001234"
                          />
                          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            11-character IFSC code
                          </p>
                        </div>
                      </div>

                      {/* Attachments Section */}
                      <h3 className="new-employee-section-header" style={{ marginTop: '2rem' }}>Attachments</h3>
                      <p className="new-employee-section-description">Upload company logos, images, and documents.</p>

                      <div className="establishment-attachments-section" style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        border: '1px dashed var(--theme-primary)',
                        borderRadius: '0.5rem',
                        background: 'var(--theme-surface)'
                      }}>
                        {/* Upload Section */}
                        <div style={{ marginBottom: '1rem' }}>
                          {/* Category Selection */}
                          <div style={{ marginBottom: '0.75rem' }}>
                            <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>
                              File Category
                            </label>
                            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input
                                  type="radio"
                                  name="fileCategory"
                                  checked={selectedCategory === null}
                                  onChange={() => setSelectedCategory(null)}
                                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                />
                                <span style={{ fontSize: '0.875rem', color: '#374151' }}>General</span>
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input
                                  type="radio"
                                  name="fileCategory"
                                  checked={selectedCategory === 'logo'}
                                  onChange={() => setSelectedCategory('logo')}
                                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                />
                                <span style={{ fontSize: '0.875rem', color: '#374151' }}>Logo</span>
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input
                                  type="radio"
                                  name="fileCategory"
                                  checked={selectedCategory === 'signature'}
                                  onChange={() => setSelectedCategory('signature')}
                                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                />
                                <span style={{ fontSize: '0.875rem', color: '#374151' }}>Signature</span>
                              </label>
                            </div>
                          </div>

                          <label
                            htmlFor="attachment-upload"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              padding: '0.625rem 1.25rem',
                              cursor: uploadingFile || !selectedEstablishmentId ? 'not-allowed' : 'pointer',
                              opacity: uploadingFile || !selectedEstablishmentId ? 0.5 : 1,
                              background: 'var(--theme-surface, #ffffff)',
                              color: '#059669',
                              border: '2px solid #059669',
                              borderRadius: '8px',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!uploadingFile && selectedEstablishmentId) {
                                e.currentTarget.style.background = '#f0fdf4';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'var(--theme-surface, #ffffff)';
                            }}
                          >
                            <FileText size={16} />
                            {uploadingFile ? 'Uploading...' : 'Choose Files'}
                          </label>
                          <input
                            id="attachment-upload"
                            type="file"
                            multiple
                            accept="*"
                            onChange={handleFileSelect}
                            disabled={uploadingFile || !selectedEstablishmentId}
                            style={{ display: 'none' }}
                          />
                          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                            All file types supported. Max 10MB per file.
                          </p>
                        </div>

                        {/* Upload Message */}
                        {uploadMessage.text && (
                          <div style={{
                            padding: '0.75rem',
                            borderRadius: '0.375rem',
                            marginBottom: '1rem',
                            background: uploadMessage.type === 'success' ? '#d1fae5' : '#fee2e2',
                            color: uploadMessage.type === 'success' ? '#065f46' : '#991b1b',
                            border: `1px solid ${uploadMessage.type === 'success' ? '#10b981' : '#ef4444'}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            {uploadMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                            {uploadMessage.text}
                          </div>
                        )}

                        {/* Attachments List */}
                        {attachments.length > 0 ? (
                          <div>
                            <h4 style={{
                              color: '#059669',
                              fontWeight: '600',
                              marginBottom: '0.75rem',
                              fontSize: '0.875rem'
                            }}>
                              Uploaded Files ({attachments.length})
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              {attachments.map((attachment) => (
                                <div
                                  key={attachment.id}
                                  className="establishment-attachment-item"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '0.75rem',
                                    background: 'var(--theme-surface, #ffffff)',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '0.375rem'
                                  }}
                                >
                                  <div className="establishment-attachment-info" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    flex: 1
                                  }}>
                                    <span style={{ fontSize: '1.5rem' }}>
                                      {getFileIcon(attachment.file_name)}
                                    </span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <a
                                          href={attachment.s3_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: '#059669',
                                            textDecoration: 'none',
                                            fontWeight: '500',
                                            fontSize: '0.875rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          {attachment.file_name}
                                        </a>
                                        <span style={{
                                          padding: '0.125rem 0.5rem',
                                          background: !attachment.category ? '#f8f9fa' : attachment.category === 'logo' ? '#dbeafe' : '#fce7f3',
                                          color: !attachment.category ? '#6b7280' : attachment.category === 'logo' ? '#1e40af' : '#9f1239',
                                          borderRadius: '0.25rem',
                                          fontSize: '0.625rem',
                                          fontWeight: '600',
                                          textTransform: 'uppercase',
                                          whiteSpace: 'nowrap'
                                        }}>
                                          {attachment.category || 'general'}
                                        </span>
                                      </div>
                                      <p style={{
                                        fontSize: '0.75rem',
                                        color: '#6b7280',
                                        marginTop: '0.125rem'
                                      }}>
                                        {formatFileSize(attachment.file_size)}
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteAttachment(attachment.id, attachment.file_name)}
                                    className="establishment-delete-attachment-btn"
                                    style={{
                                      color: '#ef4444',
                                      cursor: 'pointer',
                                      background: 'none',
                                      border: 'none',
                                      padding: '0.375rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      transition: 'color 0.2s'
                                    }}
                                    title="Delete attachment"
                                  >
                                    <X size={18} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div style={{
                            padding: '2rem',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '0.875rem'
                          }}>
                            No attachments uploaded yet
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Departments Tab */}
                  {activeTab === 'departments' && (
                    <div className="animate-fadeIn">
                      <h3 className="new-employee-section-header" style={{ marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: '600' }}>Departments</h3>
                      <p className="new-employee-section-description" style={{ marginBottom: '1.5rem', color: '#6b7280', fontSize: '0.875rem' }}>Manage departments for this organization.</p>

                      <div className="new-employee-field-group">
                        <div>
                          <label className="update-employee-field-label" style={{ display: 'flex', alignItems: 'center' }}>
                            <Briefcase size={16} style={{ marginRight: '6px' }} />
                            Add Department
                          </label>
                          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                            <input
                              type="text"
                              value={newDepartment}
                              onChange={(e) => setNewDepartment(e.target.value)}
                              onKeyPress={handleKeyPress}
                              className="update-employee-input"
                              placeholder="e.g., Sales, HR, Operations"
                              style={{ flex: 1 }}
                            />
                            <button
                              onClick={handleAddDepartment}
                              className="new-employee-btn-next"
                              style={{
                                minWidth: 'auto',
                                padding: '0.625rem 1.25rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                              type="button"
                            >
                              <Plus size={16} />
                              Add
                            </button>
                          </div>
                        </div>

                        {/* Department List */}
                        {departments.length > 0 && (
                          <div style={{ marginTop: '1.5rem' }}>
                            <h4 style={{
                              color: '#059669',
                              fontWeight: '600',
                              marginBottom: '1rem',
                              fontSize: '0.875rem'
                            }}>
                              Departments ({departments.length})
                            </h4>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                              gap: '0.75rem'
                            }}>
                              {departments.map((dept, index) => (
                                <div
                                  key={index}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '0.625rem 1rem',
                                    background: '#f0fdf4',
                                    border: '1.5px solid #10b981',
                                    borderRadius: '0.5rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    color: '#059669'
                                  }}
                                >
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Briefcase size={14} />
                                    {dept}
                                  </span>
                                  <button
                                    onClick={() => handleRemoveDepartment(dept)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0.25rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      color: '#dc2626',
                                      transition: 'color 0.2s'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.color = '#991b1b'}
                                    onMouseOut={(e) => e.currentTarget.style.color = '#dc2626'}
                                    type="button"
                                    title="Remove department"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {departments.length === 0 && (
                          <div style={{
                            marginTop: '1.5rem',
                            padding: '2rem',
                            background: 'var(--theme-surface)',
                            border: '2px dashed #d1d5db',
                            borderRadius: '0.5rem',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '0.875rem'
                          }}>
                            <Briefcase size={32} style={{ margin: '0 auto 0.75rem', opacity: 0.5 }} />
                            <p>No departments added yet. Add departments using the input above.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Postal Addresses Tab */}
                  {activeTab === 'addresses' && (
                    <div className="animate-fadeIn">
                      <h3 className="new-employee-section-header" style={{ marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: '600' }}>Postal Addresses</h3>
                      <p className="new-employee-section-description" style={{ marginBottom: '1.5rem', color: '#6b7280', fontSize: '0.875rem' }}>Update main organization and registered office addresses.</p>

                      <div style={{ marginBottom: '2rem' }}>
                        <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#374151' }}>Main Organization</h4>
                        <div className="update-employee-form-grid">
                          <div className="full-width">
                            <label className="update-employee-field-label">Location/City</label>
                            <input
                              type="text"
                              value={formData.mainLocation || ''}
                              onChange={(e) => handleInputChange('mainLocation', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                          <div className="full-width">
                            <label className="update-employee-field-label">Full Address</label>
                            <textarea
                              value={formData.mainAddress || ''}
                              onChange={(e) => handleInputChange('mainAddress', e.target.value)}
                              className="update-employee-input"
                              rows="7"
                              style={{ minHeight: '105px' }}
                            />
                          </div>
                          <div>
                            <label className="update-employee-field-label">Telephone</label>
                            <input
                              type="tel"
                              value={formData.mainTelephone || ''}
                              onChange={(e) => handleInputChange('mainTelephone', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                          <div>
                            <label className="update-employee-field-label">Fax</label>
                            <input
                              type="tel"
                              value={formData.mainFax || ''}
                              onChange={(e) => handleInputChange('mainFax', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                          <div className="full-width">
                            <label className="update-employee-field-label">E-mail</label>
                            <input
                              type="email"
                              value={formData.mainEmail || ''}
                              onChange={(e) => handleInputChange('mainEmail', e.target.value)}
                              onBlur={(e) => validateUniqueness('mainEmail', e.target.value)}
                              className={`update-employee-input ${uniqueErrors.mainEmail ? 'input-error' : ''}`}
                            />
                            {uniqueErrors.mainEmail && <span className="error-text">{uniqueErrors.mainEmail}</span>}
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#374151' }}>Registered Office</h4>
                        <div className="update-employee-form-grid">
                          <div className="full-width">
                            <label className="update-employee-field-label">Location/City</label>
                            <input
                              type="text"
                              value={formData.registeredLocation || ''}
                              onChange={(e) => handleInputChange('registeredLocation', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                          <div className="full-width">
                            <label className="update-employee-field-label">Full Address</label>
                            <textarea
                              value={formData.registeredAddress || ''}
                              onChange={(e) => handleInputChange('registeredAddress', e.target.value)}
                              className="update-employee-input"
                              rows="7"
                              style={{ minHeight: '105px' }}
                            />
                          </div>
                          <div>
                            <label className="update-employee-field-label">Telephone</label>
                            <input
                              type="tel"
                              value={formData.registeredTelephone || ''}
                              onChange={(e) => handleInputChange('registeredTelephone', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                          <div>
                            <label className="update-employee-field-label">Fax</label>
                            <input
                              type="tel"
                              value={formData.registeredFax || ''}
                              onChange={(e) => handleInputChange('registeredFax', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                          <div className="full-width">
                            <label className="update-employee-field-label">E-mail</label>
                            <input
                              type="email"
                              value={formData.registeredEmail || ''}
                              onChange={(e) => handleInputChange('registeredEmail', e.target.value)}
                              onBlur={(e) => validateUniqueness('registeredEmail', e.target.value)}
                              className={`update-employee-input ${uniqueErrors.registeredEmail ? 'input-error' : ''}`}
                            />
                            {uniqueErrors.registeredEmail && <span className="error-text">{uniqueErrors.registeredEmail}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Employer Details Tab */}
                  {activeTab === 'employer' && (
                    <div className="animate-fadeIn">
                      <h3 className="new-employee-section-header" style={{ marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: '600' }}>Employer Details</h3>
                      <p className="new-employee-section-description" style={{ marginBottom: '1.5rem', color: '#6b7280', fontSize: '0.875rem' }}>Update employer/proprietor information.</p>

                      <div className="update-employee-form-grid">
                        <div>
                          <label className="update-employee-field-label">Employer Name</label>
                          <input
                            type="text"
                            value={formData.employerName || ''}
                            onChange={(e) => handleInputChange('employerName', e.target.value)}
                            className="update-employee-input"
                          />
                        </div>
                        <div>
                          <label className="update-employee-field-label">Designation</label>
                          <input
                            type="text"
                            value={formData.employerDesignation || ''}
                            onChange={(e) => handleInputChange('employerDesignation', e.target.value)}
                            className="update-employee-input"
                          />
                        </div>
                        <div className="full-width">
                          <label className="update-employee-field-label">Residential Address</label>
                          <textarea
                            value={formData.employerResidentialAddress || ''}
                            onChange={(e) => handleInputChange('employerResidentialAddress', e.target.value)}
                            className="update-employee-input"
                            rows="7"
                            style={{ minHeight: '105px' }}
                          />
                        </div>
                        <div>
                          <label className="update-employee-field-label">Telephone</label>
                          <input
                            type="tel"
                            value={formData.employerTelephone || ''}
                            onChange={(e) => handleInputChange('employerTelephone', e.target.value)}
                            className="update-employee-input"
                          />
                        </div>
                        <div>
                          <label className="update-employee-field-label">Mobile</label>
                          <input
                            type="tel"
                            value={formData.employerMobile || ''}
                            onChange={(e) => handleInputChange('employerMobile', e.target.value)}
                            className="update-employee-input"
                          />
                        </div>
                        <div className="full-width">
                          <label className="update-employee-field-label">E-mail</label>
                          <input
                            type="email"
                            value={formData.employerEmail || ''}
                            onChange={(e) => handleInputChange('employerEmail', e.target.value)}
                            className="update-employee-input"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Management Details Tab */}
                  {activeTab === 'management' && (
                    <div className="animate-fadeIn">
                      <h3 className="new-employee-section-header" style={{ marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: '600' }}>Management Details</h3>
                      <p className="new-employee-section-description" style={{ marginBottom: '1.5rem', color: '#6b7280', fontSize: '0.875rem' }}>Update manager and authorised signatory information.</p>

                      <div style={{ marginBottom: '2rem' }}>
                        <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#374151' }}>Manager</h4>
                        <div className="update-employee-form-grid">
                          <div>
                            <label className="update-employee-field-label">Name</label>
                            <input
                              type="text"
                              value={formData.managerName || ''}
                              onChange={(e) => handleInputChange('managerName', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                          <div>
                            <label className="update-employee-field-label">Designation</label>
                            <input
                              type="text"
                              value={formData.managerDesignation || ''}
                              onChange={(e) => handleInputChange('managerDesignation', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                          <div className="full-width">
                            <label className="update-employee-field-label">Residential Address</label>
                            <textarea
                              value={formData.managerResidentialAddress || ''}
                              onChange={(e) => handleInputChange('managerResidentialAddress', e.target.value)}
                              className="update-employee-input"
                              rows="7"
                              style={{ minHeight: '105px' }}
                            />
                          </div>
                          <div>
                            <label className="update-employee-field-label">Telephone</label>
                            <input
                              type="tel"
                              value={formData.managerTelephone || ''}
                              onChange={(e) => handleInputChange('managerTelephone', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                          <div>
                            <label className="update-employee-field-label">Mobile</label>
                            <input
                              type="tel"
                              value={formData.managerMobile || ''}
                              onChange={(e) => handleInputChange('managerMobile', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                          <div className="full-width">
                            <label className="update-employee-field-label">E-mail</label>
                            <input
                              type="email"
                              value={formData.managerEmail || ''}
                              onChange={(e) => handleInputChange('managerEmail', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#374151' }}>Authorised Signatory</h4>
                        <div className="update-employee-form-grid">
                          <div>
                            <label className="update-employee-field-label">Name</label>
                            <input
                              type="text"
                              value={formData.authSignatoryName || ''}
                              onChange={(e) => handleInputChange('authSignatoryName', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                          <div>
                            <label className="update-employee-field-label">Designation</label>
                            <input
                              type="text"
                              value={formData.authSignatoryDesignation || ''}
                              onChange={(e) => handleInputChange('authSignatoryDesignation', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                          <div className="full-width">
                            <label className="update-employee-field-label">Residential Address</label>
                            <textarea
                              value={formData.authSignatoryResidentialAddress || ''}
                              onChange={(e) => handleInputChange('authSignatoryResidentialAddress', e.target.value)}
                              className="update-employee-input"
                              rows="7"
                              style={{ minHeight: '105px' }}
                            />
                          </div>
                          <div>
                            <label className="update-employee-field-label">Telephone</label>
                            <input
                              type="tel"
                              value={formData.authSignatoryTelephone || ''}
                              onChange={(e) => handleInputChange('authSignatoryTelephone', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                          <div>
                            <label className="update-employee-field-label">Mobile</label>
                            <input
                              type="tel"
                              value={formData.authSignatoryMobile || ''}
                              onChange={(e) => handleInputChange('authSignatoryMobile', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                          <div className="full-width">
                            <label className="update-employee-field-label">E-mail</label>
                            <input
                              type="email"
                              value={formData.authSignatoryEmail || ''}
                              onChange={(e) => handleInputChange('authSignatoryEmail', e.target.value)}
                              className="update-employee-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="new-employee-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem', gap: '1rem' }}>
                    <button
                      onClick={handlePrevious}
                      disabled={activeTab === 'basic'}
                      className="new-employee-btn-previous"
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: activeTab === 'basic' ? '#f8f9fa' : 'var(--theme-surface, #ffffff)',
                        color: activeTab === 'basic' ? '#9ca3af' : '#374151',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        cursor: activeTab === 'basic' ? 'not-allowed' : 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}
                    >
                      Previous
                    </button>

                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      {/* Save Button - Always visible */}
                      <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="new-employee-btn-create"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.75rem 1.5rem',
                          background: '#10b981',
                          color: 'var(--theme-surface, #ffffff)',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="update-employee-spinner"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save size={16} />
                            Save
                          </>
                        )}
                      </button>

                      {/* Next Button - Only if not last tab */}
                      {!isLastTab && (
                        <button
                          onClick={handleNext}
                          className="new-employee-btn-next"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.75rem 1.5rem',
                            background: '#3b82f6',
                            color: 'var(--theme-surface, #ffffff)',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500'
                          }}
                        >
                          Next
                          <ChevronRight size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default UpdateEstablishment;